import { formatAccountRoleLabel, type CompanyAssignedUser } from "@/lib/admin/companiesStore";
import { Mail, Phone, Shield, User } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";

type Props = {
  users: CompanyAssignedUser[];
  /** Shown above the list (e.g. "Company owner accounts"). */
  title?: string;
  description?: string;
  /** Use inside another panel: no outer card, no title/description header. */
  embedded?: boolean;
};

export function CompanyOwnerUsersSection({
  users,
  title = "Users assigned to this company",
  description = "Accounts linked via users.company_id (company-owner, employee, etc.).",
  embedded = false,
}: Props) {
  const empty = (
    <p className="text-sm text-muted-foreground rounded-lg border border-border/50 bg-muted/20 px-3 py-3 leading-relaxed">
      No users are linked to this company yet. Add an initial user when creating the company, assign company_id on user records, or use your employee
      and user-access workflow.
    </p>
  );

  if (users.length === 0) {
    if (embedded) {
      return <div className="space-y-4">{empty}</div>;
    }
    return (
      <div className="glass-card p-6">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-primary shrink-0" />
          <h3 className="font-display font-semibold text-foreground text-sm">{title}</h3>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
        <div className="mt-4">{empty}</div>
      </div>
    );
  }

  const list = (
    <ul className="space-y-4">
      {users.map((u) => (
        <li key={u.id} className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-3 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border/50 bg-background/80">
              <User className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">{u.name.trim() || "—"}</p>
              <p className="text-[11px] font-medium text-muted-foreground mt-0.5">
                {formatAccountRoleLabel(u.role)} <span className="text-muted-foreground/80">· ID {u.id}</span>
              </p>
            </div>
          </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2 min-w-0">
                <Mail className="w-4 h-4 shrink-0 text-muted-foreground mt-0.5" />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Email</p>
                  <p className="text-foreground break-all">{u.email?.trim() || "—"}</p>
                </div>
              </div>
              <div className="flex items-start gap-2 min-w-0">
                <Phone className="w-4 h-4 shrink-0 text-muted-foreground mt-0.5" />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Phone</p>
                  <p className="text-foreground">{u.phone?.trim() || "—"}</p>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Added{" "}
              {isValid(parseISO(u.createdAt)) ? format(parseISO(u.createdAt), "PP") : u.createdAt}
            </p>
        </li>
      ))}
    </ul>
  );

  if (embedded) {
    return <div className="space-y-4">{list}</div>;
  }

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="w-4 h-4 text-primary shrink-0" />
        <h3 className="font-display font-semibold text-foreground text-sm">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed mb-4">{description}</p>
      {list}
    </div>
  );
}
